import {showError, showInfo, showWarning} from './utils.ts';
import {DataManager} from "./DataManager.ts";

export class AccountModal {
  private readonly modal: HTMLElement;
  private readonly backdrop: HTMLElement;
  private readonly registrationForm: HTMLElement;
  private readonly accountInfoForm: HTMLElement;
  private readonly registerLoginInput: HTMLInputElement;
  private readonly registerPasswordInput: HTMLInputElement;
  private readonly registerBtn: HTMLButtonElement;
  private readonly accountUsernameLabel: HTMLElement;
  private readonly currentPasswordInput: HTMLInputElement;
  private readonly newPasswordInput: HTMLInputElement;
  private readonly repeatPasswordInput: HTMLInputElement;
  private readonly changePasswordBtn: HTMLButtonElement;
  private readonly closeAccountModalBtn: HTMLButtonElement;
  private readonly closeAccountInfoBtn: HTMLButtonElement;
  private readonly exitAccountBtn: HTMLButtonElement;

  constructor(
    private readonly dataManager: DataManager,
    private readonly onAccountStatusChanged?: () => void,
  ) {
    this.backdrop = document.getElementById('accountModalBackdrop') as HTMLElement;
    this.modal = document.getElementById('accountModal') as HTMLElement;
    this.registrationForm = document.getElementById('registrationForm') as HTMLElement;
    this.accountInfoForm = document.getElementById('accountInfoForm') as HTMLElement;
    this.registerLoginInput = document.getElementById('registerLogin') as HTMLInputElement;
    this.registerPasswordInput = document.getElementById('registerPassword') as HTMLInputElement;
    this.registerBtn = document.getElementById('registerBtn') as HTMLButtonElement;
    this.accountUsernameLabel = document.getElementById('accountUsername') as HTMLElement;
    this.currentPasswordInput = document.getElementById('currentPassword') as HTMLInputElement;
    this.newPasswordInput = document.getElementById('newPassword') as HTMLInputElement;
    this.repeatPasswordInput = document.getElementById('repeatPassword') as HTMLInputElement;
    this.changePasswordBtn = document.getElementById('changePasswordBtn') as HTMLButtonElement;
    this.closeAccountModalBtn = document.getElementById('closeAccountModalBtn') as HTMLButtonElement;
    this.closeAccountInfoBtn = document.getElementById('closeAccountInfoBtn') as HTMLButtonElement;
    this.exitAccountBtn = document.getElementById('exitAccountBtn') as HTMLButtonElement;

    this.setupEventListeners();
  }

  private setupEventListeners(): void {
    this.registerBtn.addEventListener('click', () => this.handleRegister());
    this.changePasswordBtn.addEventListener('click', () => this.handleChangePassword());
    this.closeAccountModalBtn.addEventListener('click', () => this.hide());
    this.closeAccountInfoBtn.addEventListener('click', () => this.hide());
    this.exitAccountBtn.addEventListener('click', () => this.handleExit());

    this.backdrop.addEventListener('click', () => {
      this.hide();
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isVisible()) {
        this.hide();
      }
    });

    // Handle Enter key in forms
    const registrationForm = this.registrationForm;
    registrationForm.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !this.registrationForm.classList.contains('d-none')) {
        e.preventDefault();
        this.handleRegister();
      }
    });

    const accountInfoForm = this.accountInfoForm;
    accountInfoForm.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !this.accountInfoForm.classList.contains('d-none')) {
        e.preventDefault();
        this.handleChangePassword();
      }
    });
  }

  public show(): void {
    const isLoggedIn = this.dataManager.isLoggedIn();
    
    if (isLoggedIn) {
      this.showAccountInfo();
    } else {
      this.showRegistration();
    }
  }

  private showRegistration(): void {
    this.registrationForm.classList.remove('d-none');
    this.accountInfoForm.classList.add('d-none');
    this.registerLoginInput.value = '';
    this.registerPasswordInput.value = '';
    
    this.backdrop.style.display = 'block';
    this.modal.style.display = 'block';
    this.registerLoginInput.focus();
  }

  private async showAccountInfo(): Promise<void> {
    this.registrationForm.classList.add('d-none');
    this.accountInfoForm.classList.remove('d-none');
    this.currentPasswordInput.value = '';
    this.newPasswordInput.value = '';
    this.repeatPasswordInput.value = '';

  this.accountUsernameLabel.textContent = this.dataManager.getUserName()
    
    this.backdrop.style.display = 'block';
    this.modal.style.display = 'block';
    this.currentPasswordInput.focus();
  }

  private async handleRegister(): Promise<void> {
    if (this.registerBtn.disabled) {
      return;
    }

    const login = this.registerLoginInput.value.trim();
    const password = this.registerPasswordInput.value;

    if (!login) {
      showWarning('Please enter a login');
      this.registerLoginInput.focus();
      return;
    }

    if (!password) {
      showWarning('Please enter a password');
      this.registerPasswordInput.focus();
      return;
    }

    this.registerBtn.disabled = true;
    this.registerBtn.textContent = 'Registering...';

    try {
      const success = await this.dataManager.signUp(login, password);
      if (success) {
        showInfo('Registration successful');
        this.hide();
        this.onAccountStatusChanged?.();
      }
    } catch (error: any) {
      showError(error.message || 'Registration failed');
    } finally {
      this.registerBtn.disabled = false;
      this.registerBtn.textContent = 'Register';
    }
  }

  private async handleChangePassword(): Promise<void> {
    if (this.changePasswordBtn.disabled) {
      return;
    }

    const currentPassword = this.currentPasswordInput.value;
    const newPassword = this.newPasswordInput.value;
    const repeatPassword = this.repeatPasswordInput.value;

    if (!currentPassword) {
      showWarning('Please enter current password');
      this.currentPasswordInput.focus();
      return;
    }

    if (!newPassword) {
      showWarning('Please enter new password');
      this.newPasswordInput.focus();
      return;
    }

    if (newPassword !== repeatPassword) {
      showWarning('New passwords do not match');
      this.repeatPasswordInput.focus();
      return;
    }

    this.changePasswordBtn.disabled = true;
    this.changePasswordBtn.textContent = 'Changing...';

    try {
      const success = await this.dataManager.changePassword(currentPassword, newPassword);
      if (success) {
        showInfo('Password changed successfully');
        this.hide();
      }
    } catch (error: any) {
      showError(error.message || 'Failed to change password');
    } finally {
      this.changePasswordBtn.disabled = false;
      this.changePasswordBtn.textContent = 'Change Password';
    }
  }

  public isVisible(): boolean {
    return this.modal.style.display !== 'none';
  }

  private async handleExit(): Promise<void> {
    if (!this.dataManager.isLoggedIn()) {
      showWarning('You are not logged in');
      return;
    }

    this.exitAccountBtn.disabled = true;
    try {
        await this.dataManager.exit();
        showInfo('Account exited successfully');
        this.hide();
        window.location.reload();
    } finally {
        this.exitAccountBtn.disabled = false;
    }
  }

  public hide(): void {
    this.backdrop.style.display = 'none';
    this.modal.style.display = 'none';
    this.registerLoginInput.value = '';
    this.registerPasswordInput.value = '';
    this.currentPasswordInput.value = '';
    this.newPasswordInput.value = '';
    this.repeatPasswordInput.value = '';
  }
}

